<script setup lang="ts">
const { loggedIn, user, logout } = useAuth()
const { theme, set } = useTheme()
const route = useRoute()
const api = useApi()
const { subscribeToUser } = useRealtime()
const userMenuOpen = ref(false)
const userMenuRef = ref<HTMLElement | null>(null)
const hydrated = ref(false)
const unreadCount = useState<number>('unread-notifications', () => 0)
let unsubscribeUser: (() => void) | null = null

const desktopLinks = [
    { to: '/home', label: 'Utama' },
    { to: '/lists', label: 'Senarai' },
    { to: '/history', label: 'Sejarah' },
    { to: '/dashboard', label: 'Statistik' },
]

function toggleTheme(): void {
    set(theme.value === 'dark' ? 'light' : 'dark')
}

function closeUserMenu(): void {
    userMenuOpen.value = false
}

async function logoutFromMenu(): Promise<void> {
    closeUserMenu()
    await logout()
}

function onDocumentPointerDown(event: PointerEvent): void {
    if (!userMenuOpen.value || userMenuRef.value?.contains(event.target as Node)) {
        return
    }

    closeUserMenu()
}

async function refreshUnreadNotifications(): Promise<void> {
    if (!loggedIn.value) {
        return
    }

    try {
        const response = await api.get<{ meta: { unread_count?: number } }>('/notifications', {
            per_page: 1,
        })
        unreadCount.value = response.meta.unread_count ?? 0
    } catch {
        // Header notification count is non-critical.
    }
}

onMounted(() => {
    hydrated.value = true
    document.addEventListener('pointerdown', onDocumentPointerDown)
    refreshUnreadNotifications()

    if (user.value) {
        unsubscribeUser = subscribeToUser(user.value.id, () => {
            unreadCount.value += 1
        })
    }
})

onBeforeUnmount(() => {
    document.removeEventListener('pointerdown', onDocumentPointerDown)
    unsubscribeUser?.()
})

watch(() => route.fullPath, closeUserMenu)

const topbarStyle = computed(() => ({
    top: '12px',
    width: 'min(1500px, calc(100% - 32px))',
    border: '1px solid color-mix(in srgb, var(--line), transparent 8%)',
    borderRadius: '999px',
    background: 'color-mix(in srgb, var(--panel), transparent 8%)',
    boxShadow: 'var(--shadow-soft)',
    backdropFilter: 'blur(18px)',
}))
</script>

<template>
    <header
        class="topbar-in topbar-responsive sticky z-30 mx-auto mb-6 flex items-center justify-between gap-3.5 px-2.5 py-2.5"
        :style="topbarStyle"
    >
        <!-- Logo -->
        <NuxtLink
            :to="loggedIn ? '/home' : '/'"
            class="flex min-w-0 items-center gap-2.5 pl-1"
            aria-label="Groceries – halaman utama"
        >
            <span
                aria-hidden="true"
                class="grid size-9 shrink-0 place-items-center rounded-[13px] text-xs font-extrabold tracking-[-.04em]"
                :style="{
                    background: 'var(--brand)',
                    color: 'var(--panel)',
                    boxShadow: '0 12px 24px rgba(10,12,14,.14)',
                }"
            >
                GR
            </span>
            <span class="min-w-0">
                <strong class="block text-sm tracking-[-.03em]" :style="{ color: 'var(--ink)' }"
                    >Groceries</strong
                >
                <span
                    class="hidden whitespace-nowrap text-xs sm:block"
                    :style="{ color: 'var(--muted)' }"
                    >Senarai belanja pintar</span
                >
            </span>
        </NuxtLink>

        <!-- Desktop nav (hidden on mobile, flex on md+) -->
        <nav
            v-if="loggedIn"
            class="scrollbar-hide hidden items-center gap-1 overflow-x-auto md:flex"
            aria-label="Navigasi desktop"
        >
            <NuxtLink
                v-for="link in desktopLinks"
                :key="link.to"
                :to="link.to"
                class="inline-flex min-h-9 items-center justify-center whitespace-nowrap rounded-full px-3 text-[13px] font-semibold transition-all duration-[.18s]"
                :style="{
                    background: route.path.startsWith(link.to)
                        ? 'var(--panel-strong)'
                        : 'transparent',
                    color: route.path.startsWith(link.to) ? 'var(--ink)' : 'var(--muted)',
                }"
                :aria-current="route.path.startsWith(link.to) ? 'page' : undefined"
            >
                {{ link.label }}
            </NuxtLink>
        </nav>

        <!-- Actions -->
        <nav
            ref="userMenuRef"
            class="relative flex shrink-0 items-center gap-1"
            aria-label="Tindakan"
        >
            <template v-if="loggedIn">
                <button
                    type="button"
                    class="inline-flex min-h-11 items-center gap-2 rounded-full px-2.5 pr-3 text-sm font-semibold transition-all"
                    :style="{
                        background: userMenuOpen ? 'var(--panel-strong)' : 'transparent',
                        color: 'var(--ink)',
                    }"
                    :aria-expanded="userMenuOpen"
                    aria-haspopup="menu"
                    aria-label="Buka menu akaun"
                    @click="userMenuOpen = !userMenuOpen"
                >
                    <span
                        class="flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-extrabold"
                        :style="{ background: 'var(--brand)', color: 'var(--panel)' }"
                    >
                        {{ user?.name?.charAt(0)?.toUpperCase() ?? '?' }}
                    </span>
                    <span class="hidden max-w-[120px] truncate sm:block">{{
                        user?.name ?? 'Akaun'
                    }}</span>
                    <span aria-hidden="true" class="text-xs" :style="{ color: 'var(--muted)' }"
                        >⌄</span
                    >
                </button>

                <div
                    v-if="userMenuOpen"
                    class="absolute right-0 top-[calc(100%+10px)] z-50 w-64 rounded-3xl p-2"
                    :style="{
                        background: 'color-mix(in srgb, var(--panel), transparent 3%)',
                        border: '1px solid var(--line)',
                        boxShadow: 'var(--shadow)',
                        backdropFilter: 'blur(18px)',
                    }"
                    role="menu"
                >
                    <div class="px-3 py-2">
                        <p class="truncate text-sm font-bold">{{ user?.name }}</p>
                        <p class="truncate text-xs" :style="{ color: 'var(--muted)' }">
                            {{ user?.email }}
                        </p>
                    </div>

                    <button
                        type="button"
                        class="flex min-h-11 w-full items-center gap-3 rounded-2xl px-3 text-left text-sm font-semibold"
                        :style="{ color: 'var(--ink)' }"
                        role="menuitem"
                        @click="toggleTheme"
                    >
                        <AppIcon
                            :name="hydrated ? (theme === 'dark' ? 'sun' : 'moon') : 'moon'"
                            class="size-4"
                        />
                        {{
                            hydrated ? (theme === 'dark' ? 'Mod cerah' : 'Mod gelap') : 'Mod gelap'
                        }}
                    </button>

                    <NuxtLink
                        to="/notifications"
                        class="flex min-h-11 w-full items-center gap-3 rounded-2xl px-3 text-left text-sm font-semibold"
                        :style="{ color: 'var(--ink)' }"
                        role="menuitem"
                        @click="closeUserMenu"
                    >
                        <AppIcon name="bell" class="size-4" />
                        <span class="flex-1">Notifikasi</span>
                        <span
                            v-if="unreadCount > 0"
                            class="rounded-full px-2 py-0.5 text-[10px] font-extrabold"
                            :style="{ background: 'var(--red-soft)', color: 'var(--red)' }"
                        >
                            {{ unreadCount > 9 ? '9+' : unreadCount }}
                        </span>
                    </NuxtLink>

                    <NuxtLink
                        to="/profile"
                        class="flex min-h-11 w-full items-center gap-3 rounded-2xl px-3 text-left text-sm font-semibold"
                        :style="{ color: 'var(--ink)' }"
                        role="menuitem"
                        @click="closeUserMenu"
                    >
                        <span class="grid size-4 place-items-center text-xs">F</span>
                        Profil & tetapan
                    </NuxtLink>

                    <button
                        type="button"
                        class="mt-1 flex min-h-11 w-full items-center gap-3 rounded-2xl px-3 text-left text-sm font-semibold"
                        :style="{ color: 'var(--red)' }"
                        role="menuitem"
                        @click="logoutFromMenu"
                    >
                        <AppIcon name="x" class="size-4" />
                        Log keluar
                    </button>
                </div>
            </template>

            <template v-else>
                <button
                    type="button"
                    class="ghost-icon"
                    :aria-label="
                        hydrated
                            ? theme === 'dark'
                                ? 'Tukar ke mod cerah'
                                : 'Tukar ke mod gelap'
                            : 'Tukar ke mod gelap'
                    "
                    @click="toggleTheme"
                >
                    <AppIcon
                        :name="hydrated ? (theme === 'dark' ? 'sun' : 'moon') : 'moon'"
                        class="size-4"
                    />
                </button>

                <NuxtLink
                    to="/auth/login"
                    class="btn !min-h-9 !px-3 text-[13px]"
                    :style="{ color: 'var(--muted)' }"
                >
                    Log masuk
                </NuxtLink>
                <NuxtLink to="/auth/register" class="btn-primary !min-h-9 !px-3 text-[13px]">
                    Daftar
                </NuxtLink>
            </template>
        </nav>
    </header>
</template>
